import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { RouteComponentProps, withRouter, Link } from 'react-router-dom';
import { ToxnotSignInRequiredComponent } from '../../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import { TeamRole } from '../../../store/Models/UserContext';
import { AppServices } from '../../../AppServices';
import Layout from '../../Layout';
import { ToxnotServiceClient } from '../../../store/ToxnotServiceClient';
import { SftpImportConfigurationWorkflow, ImportType, InventoryMode, DataType, CalculationModeType, InventoryIdentificationMatchMode, SupplierIdentificationMatchMode, LibraryImportExistingInventoryModeType, ImportHierarchyOption, LibraryImportNonSummaryRowsModeType, SftpRecentRunHistory } from '../../../store/Models/Api/SftpImportModels';
import { ConfigurationDetailsPanel } from '../panels/ConfigurationDetailsPanel';
import { DefaultHandlingPanel } from '../panels/DefaultHandlingPanel';
import { ImportLibrarySettingsPanel } from '../panels/ImportLibrarySettingsPanel';
import { ConfigureLevelsPanel } from '../panels/ConfigureLevelsPanel';
import { ColumnMappingsPanel } from '../panels/ColumnMappingsPanel';
import { RecentRunHistoryTable } from '../panels/RecentRunHistoryTable';
import { UserContext } from '../../../store/Models/UserContext';

type SftpDetailProps = {
    appServices: AppServices;
} & RouteComponentProps<{ configId: string }>;

interface SftpDetailState {
    config: SftpImportConfigurationWorkflow | null;
    isLoading: boolean;
    errorMessage: string;
    userSettings: UserContext | null;
    recentRunHistory: SftpRecentRunHistory[] | null;
}

class SftpImportConfigurationDetail extends ToxnotSignInRequiredComponent<SftpDetailProps, SftpDetailState> {
    private serviceClient = new ToxnotServiceClient();

    constructor(props: SftpDetailProps) {
        super(props);
        this.state = {
            config: null,
            isLoading: false,
            errorMessage: '',
            userSettings: null,
            recentRunHistory: null
        };
    }

    public async componentDidMount() {
        await this.loadConfiguration();
        await this.loadUserSettings();
        await this.loadRunHistory();
    }

    private loadUserSettings = async () => {
        this.setState({ isLoading: true, errorMessage: '' });

        try {
            const response = await this.serviceClient.getUserContext();

            if (response.success && response.data) {
                const userSettings = response.data.userContextData as UserContext;
                this.setState({
                    userSettings: userSettings,
                    isLoading: false
                });
            }
        }
        catch (error) {
            this.setState({
                isLoading: false,
                errorMessage: this.localizeString("Failed to load user settings. Please try again.")
            })
        }
    };

    private loadConfiguration = async () => {
        const { configId } = this.props.match.params;
        
        this.setState({ isLoading: true, errorMessage: '' });
        
        try {
            const response = await this.serviceClient.sftpImportConfigurationGet(configId);
            
            if (response.success && response.data) {
                const data = { ...response.data } as SftpImportConfigurationWorkflow;
                // Set default enum values if undefined (TypeScript handles string enum names automatically)
                data.inventoryIdentificationMatchMode = data.inventoryIdentificationMatchMode ?? InventoryIdentificationMatchMode.Default;
                data.supplierIdentificationMatchMode = data.supplierIdentificationMatchMode ?? SupplierIdentificationMatchMode.Default;
                data.calculationMode = data.calculationMode ?? CalculationModeType.ToxnotStandardV2;
                data.importHierarchyOptionType = data.importHierarchyOptionType ?? ImportHierarchyOption.LevelColumn;
                data.libraryImportExistingInventoryMode = data.libraryImportExistingInventoryMode ?? LibraryImportExistingInventoryModeType.OverwriteExisting;
                
                // Set defaults for the two library import fields
                data.libraryImportShouldUpdateSummaryRows = data.libraryImportShouldUpdateSummaryRows ?? false;
                data.libraryImportNonSummaryRowsMode = data.libraryImportNonSummaryRowsMode ?? LibraryImportNonSummaryRowsModeType.LeaveNonSummaryRowsIntact;
                this.setState({ 
                    config: data,
                    isLoading: false 
                });
            } else {
                this.setState({ 
                    errorMessage: this.localizeString('Failed to load configuration. Please try again.'),
                    isLoading: false 
                });
            }
        } catch (error) {
            this.setState({ 
                errorMessage: this.localizeString('Error loading configuration: ') + (error as Error).message,
                isLoading: false 
            });
        }
    };

    private loadRunHistory = async () => {
        const { configId } = this.props.match.params;
        
        this.setState({ isLoading: true, errorMessage: '' });
        
        try {
            const response = await this.serviceClient.GetSftpRecentRunHistory(configId);
            
            if (response.success && response.data) {
                const data = response.data as SftpRecentRunHistory[];
                
                this.setState({ 
                    recentRunHistory: data,
                    isLoading: false 
                });
            } else {
                this.setState({ 
                    errorMessage: this.localizeString('Failed to load configuration. Please try again.'),
                    isLoading: false 
                });
            }
        } catch (error) {
            this.setState({ 
                errorMessage: this.localizeString('Error loading configuration: ') + (error as Error).message,
                isLoading: false 
            });
        }
    };

    protected getDisallowedRoles(): TeamRole[] {
        return [];
    }


    protected renderConcreteComponent() {
        const { config, isLoading, errorMessage, userSettings, recentRunHistory } = this.state;

        if (isLoading) {
            return (
                <Layout options={{ pinAppContentToWindowHeight: false }}>
                    <div className="tn-app-content-view-scroll-wrapper" style={{ height: '100%', overflowY: 'auto' }}>
                        <div className="tn-app-content-view">
                            <div className="tn-app-sections-container">
                                <div className="tn-section">
                                    <div className="tn-section-row">
                                        <div className="tn-section-col-12 text-center">
                                            <div className="spinner">{this.localizeString("Loading configuration...")}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Layout>
            );
        }

        if (errorMessage || !config || !userSettings || !recentRunHistory) {
            return (
                <Layout options={{ pinAppContentToWindowHeight: false }}>
                    <div className="tn-app-content-view-scroll-wrapper" style={{ height: '100%', overflowY: 'auto' }}>
                        <div className="tn-app-content-view">
                            <div className="tn-app-sections-container">
                                <div className="tn-section">
                                    <div className="tn-section-row">
                                        <div className="tn-section-col-12">
                                            <a href="/Developer" className="btn btn-default">{this.localizeString("Back")}</a>
                                        </div>
                                    </div>
                                    <div className="tn-section-row">
                                        <div className="tn-section-col-12">
                                            <div className="alert alert-danger">
                                                {errorMessage || this.localizeString('Configuration not found.')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Layout>
            );
        }

        return (
            <Layout options={{ pinAppContentToWindowHeight: false }}>
                <div className="tn-app-content-view-scroll-wrapper" style={{ height: '100%', overflowY: 'auto' }}>
                    <div className="tn-app-content-view">
                        <div className="tn-app-sections-container">
                            <div className="tn-section">
                                <div className="tn-section-row">
                                    <div className="tn-section-col-8">
                                        <a href="/Developer" className="btn btn-default">{this.localizeString("Back")}</a>
                                    </div>
                                    <div className="tn-section-col-4 text-right">
                                        <Link
                                            to={`/Developer/Sftp/AddOrEdit/${this.props.match.params.configId}`}
                                            className={`btn btn-default ${!userSettings.isEditor ? 'disabled' : ''}`}
                                        >
                                            <span className="glyphicon glyphicon-pencil" style={{ marginRight: '5px' }}></span>
                                            {this.localizeString("Edit")}
                                        </Link>
                                    </div>
                                </div>
                                <div className="tn-section-row">
                                    <div className="tn-section-col-12">
                                        <h3>{config.configurationName}</h3>
                                    </div>
                                </div>
                                <div className="tn-section-row" style={{ paddingTop: '15px' }}>
                                    <div className="tn-section-col-12">
                                        <ConfigurationDetailsPanel 
                                            config={config} 
                                        />
                                    </div>
                                </div>
                            </div>

                            {(config.importType === ImportType.Suppliers || config.dataType === DataType.Bom) && (
                                <div className="tn-section">
                                    <div className="tn-section-row">
                                        <div className="tn-section-col-12">
                                            <DefaultHandlingPanel config={config} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {config.importType !== ImportType.Suppliers && config.inventoryMode === InventoryMode.Multiple && config.dataType === DataType.Bom && (
                                <div className="tn-section">
                                    <div className="tn-section-row">
                                        <div className="tn-section-col-12">
                                            <ImportLibrarySettingsPanel config={config} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {config.importType !== ImportType.Suppliers && config.dataType === DataType.Bom && (
                                <div className="tn-section">
                                    <div className="tn-section-row">
                                        <div className="tn-section-col-12">
                                            <ConfigureLevelsPanel config={config} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(config.importType === ImportType.Suppliers || config.dataType === DataType.Bom) && (
                                <div className="tn-section">
                                    <div className="tn-section-row">
                                        <div className="tn-section-col-12">
                                            <ColumnMappingsPanel config={config} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <RecentRunHistoryTable recentRuns={recentRunHistory} />
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }
}

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices }),
    null
)(withRouter(SftpImportConfigurationDetail));